import { useEffect, useRef, MutableRefObject, Dispatch, SetStateAction } from 'react';
import { WebSocketAdapter } from '../utils/WebSocketAdapter';
import RoomSessionStorage from '../utils/sessionStorage';
import { getRoomCodeFromUrl, getHostCodeFromUrl, formatRoomCode, clearQueryParamFromUrl, generateRoomCode } from '../utils/roomCode';
import { clearEntryRouteFromUrl, isHostEntryPath, isImplicitHostCreatePath, isJoinEntryPath, navigateToJoin, navigateToRoot } from '../utils/entryRoutes';
import { config } from '../config';
import { t, translateBackendError, JOIN_ROOM_NOT_FOUND_MESSAGE } from '../i18n/helpers';
import { dispatchJoinRoomError } from '../utils/joinRoomEvents';
import { emitRoomRestore } from '../utils/restoreRoomSession';
import {
  playRoomCreated,
  playRoomJoined,
  playPlayerJoined,
  playGameStart,
  playGameFinished,
  playReturnToWaiting,
} from '../utils/sounds';
import {
  mergePlayersWithoutScoreRegression,
  syncPlayersFromLeaderboard,
  applyAuthoritativePlayerTotal,
} from '../utils/leaderboardUtils';
import {
  isLocalPlayerConnectedInState,
  shouldSkipReconnectEmit,
} from '../utils/roomMembership';
import type { RoomState } from '../types/room';

interface UseSocketEventsOptions {
  socket: WebSocketAdapter | null;
  roomState: RoomState | null;
  setRoomState: Dispatch<SetStateAction<RoomState | null>>;
  setConnected: Dispatch<SetStateAction<boolean>>;
  setMyPlayerId: Dispatch<SetStateAction<string | null>>;
  setIsHost: Dispatch<SetStateAction<boolean>>;
  setShowJoinRoom: Dispatch<SetStateAction<boolean>>;
  showJoinRoom: boolean;
  setReconnecting: (value: boolean) => void;
  setReconnectAttempts: (value: number) => void;
  roomStateRef: MutableRefObject<RoomState | null>;
  isLeavingRef: MutableRefObject<boolean>;
  /**
   * When true, skip auto get-room-state / room-state restore (intentional leave).
   * Premium hostToken may still be preserved for manual "Have a code?" re-entry.
   */
  suppressSessionRestoreRef?: MutableRefObject<boolean>;
  /**
   * Set true on disconnect while in a room. Forces join-room even if stale
   * local roomState still exists (phone lock soft reconnect in lobby).
   */
  needsRejoinRef: MutableRefObject<boolean>;
  reconnectTimeoutRef: MutableRefObject<NodeJS.Timeout | null>;
  pendingStartGameRef: MutableRefObject<{ gameType: string } | null>;
  isHost: boolean;
  showToast: (message: string, type?: 'success' | 'error' | 'warning' | 'info', duration?: number) => void;
  startReconnection: () => void;
  clearReconnection: () => void;
  connectionErrorAttemptsRef: MutableRefObject<number>;
  setConnectionError: Dispatch<SetStateAction<string | null>>;
  failedRoomsRef?: MutableRefObject<Set<string>>;
  /** When true, game setup should show tiles immediately (restore failed or not applicable) instead of waiting max loader time */
  setRestoreAttemptSettled?: (value: boolean) => void;
  /** Called when backend says we're on wrong DO (Cloudflare); reconnect socket with this room and retry */
  onReconnectTo?: (roomName: string) => void;
  /**
   * Create room on the correct DO after connect (Cloudflare).
   * Prefer this over emit+onReconnectTo so create-room is not sent on a closing socket.
   */
  onScheduleCreateRoom?: (payload: {
    gameType: string;
    roomCode: string;
    hostToken?: string;
    previousRoomName?: string;
  }) => void;
  /** Called when ?host= deep-link creation finishes (success or failure) so UI can drop the loader */
  onHostDeepLinkSettled?: () => void;
}

/**
 * Custom hook to handle all WebSocket event listeners
 * Centralizes all socket event handling logic
 */
export function useSocketEvents({
  socket,
  roomState,
  setRoomState,
  setConnected,
  setMyPlayerId,
  setIsHost,
  setShowJoinRoom,
  showJoinRoom,
  setReconnecting,
  setReconnectAttempts,
  roomStateRef,
  isLeavingRef,
  suppressSessionRestoreRef,
  needsRejoinRef,
  reconnectTimeoutRef,
  pendingStartGameRef,
  isHost,
  showToast,
  startReconnection,
  clearReconnection,
  connectionErrorAttemptsRef,
  setConnectionError,
  failedRoomsRef,
  setRestoreAttemptSettled,
  onReconnectTo,
  onScheduleCreateRoom,
  onHostDeepLinkSettled,
}: UseSocketEventsOptions) {
  // Track if we've already shown the "waiting for reconnection" toast
  // This prevents showing both "waiting" and "returning to home" toasts
  const hostDisconnectedToastShownRef = useRef(false);
  // Track previous hostDisconnectedAt state to detect host reconnection
  const previousHostDisconnectedAtRef = useRef<number | null | undefined>(undefined);
  // Show "Room not found" toast only once per join flow; cap reconnection after 3 attempts
  const roomNotFoundToastShownRef = useRef(false);
  const roomNotFoundAttemptsRef = useRef(0);
  const ROOM_NOT_FOUND_MAX_ATTEMPTS = 3;
  const showJoinRoomRef = useRef(showJoinRoom);
  showJoinRoomRef.current = showJoinRoom;
  // Cap reconnection join/get-room-state emissions to 3 so we never spam the backend
  const reconnectEmitCountRef = useRef(0);
  // Never show the same room-error toast twice in quick succession (e.g. "Already in room" x3 on refresh)
  const lastRoomErrorToastRef = useRef<{ message: string; at: number }>({ message: '', at: 0 });
  const ROOM_ERROR_TOAST_DEBOUNCE_MS = 3000;
  // Dedupe "game type not available" toast (BE may be rate-limited but FE still only show once per 3s)
  const lastGameTypeNotAllowedToastRef = useRef(0);
  const GAME_TYPE_NOT_ALLOWED_TOAST_DEBOUNCE_MS = 3000;
  // Throttle lobby auto-rejoin when room-state shows we were dropped
  const lastAutoRejoinAtRef = useRef(0);
  const AUTO_REJOIN_THROTTLE_MS = 3000;
  // SFX only on host; ref so handlers always see current value
  const isHostRef = useRef(isHost);
  isHostRef.current = isHost;

  useEffect(() => {
    if (!socket) return;

    // Helper to update room state safely
    const updateRoomState = (updater: (prev: RoomState | null) => RoomState | null) => {
      setRoomState(updater);
    };

    // Helper to update room state for specific room
    const updateRoomStateForRoom = (
      roomName: string,
      updater: (prev: RoomState) => RoomState
    ) => {
      setRoomState((prev) => {
        if (prev && prev.roomName === roomName) {
          return updater(prev);
        }
        return prev;
      });
    };

    // ========== CONNECTION EVENTS ==========

    const handleConnect = (_data?: any, options?: { silent?: boolean }) => {
      if (!options?.silent) {
        console.log('Connected to backend');
      }
      setConnected(true);
      // Do not set myPlayerId from socket.id here — after reconnect the stable player id
      // comes from room-joined / room-state. Setting socket.id early defeats RoomLoadingShell
      // and can make PlayerDisplay treat the wrong id as "me".

      // Clear reconnection state and connection error on successful connection
      clearReconnection();
      connectionErrorAttemptsRef.current = 0;
      setConnectionError(null);

      // Reconnection logic: prioritize typed room code (from URL) over sessionStorage
      // Check URL first, then fall back to sessionStorage if not expired
      const roomCodeFromUrl = getRoomCodeFromUrl();
      const hostCodeFromUrl = getHostCodeFromUrl();
      const savedData = RoomSessionStorage.getSavedRoomData();

      // Intentional leave: do not auto-restore even if premium hostToken was preserved
      if (isLeavingRef.current || suppressSessionRestoreRef?.current) {
        console.log('Skipping reconnect on connect - user intentionally left');
        return;
      }
      
      // IMPORTANT: If we have a host parameter in URL, we're trying to create a new admin room
      // Don't try to reconnect to old rooms in this case - let the room creation logic handle it
      if (hostCodeFromUrl) {
        console.log(`Host parameter in URL (${hostCodeFromUrl}) - skipping reconnection to allow room creation`);
        // Clear any old room data that doesn't match the host code
        if (savedData.roomName && savedData.roomName !== hostCodeFromUrl) {
          console.log(`Clearing old room data (${savedData.roomName}) - creating new admin room (${hostCodeFromUrl})`);
          RoomSessionStorage.clearRoomSessionIdentity();
        }
        return; // Don't attempt reconnection - room creation will happen via URL param handler
      }

      // /host auto-create always makes a new room — never restore a stale session from before deploy
      if (isImplicitHostCreatePath()) {
        console.log('On /host auto-create path - skipping session restore');
        if (savedData.roomName) {
          RoomSessionStorage.clearRoomSessionIdentity();
        }
        return;
      }
      
      // Check if saved room is expired
      const isSavedRoomExpired = savedData.expiresAt ? Date.now() > savedData.expiresAt : false;
      
      // Clear expired room data immediately if expired
      if (isSavedRoomExpired && savedData.roomName) {
        console.log(`Clearing expired room data on connect: ${savedData.roomName} expired at ${new Date(savedData.expiresAt!).toISOString()}`);
        RoomSessionStorage.clearRoomSessionIdentity();
        // Don't attempt reconnection if room is expired
        return;
      }
      
      // When user is on the join screen without a restorable session, JoinRoom handles join on click.
      // If they have saved player/host data (e.g. refresh on /join), still reconnect from here.
      const hasRestorableSession =
        !!savedData.roomName &&
        !isSavedRoomExpired &&
        (!!savedData.hostToken || !!savedData.playerName);

      if (showJoinRoomRef.current && !hasRestorableSession) {
        console.log('On join screen without saved session - JoinRoom handles join');
        return;
      }

      // Priority: 1. URL room code (user typed it), 2. sessionStorage (if not expired)
      const roomRaw = roomCodeFromUrl || (!isSavedRoomExpired && savedData.roomName ? savedData.roomName : null);
      const roomToReconnect = roomRaw ? formatRoomCode(roomRaw) : null;

      // Skip reconnection for rooms that recently failed (e.g. "Room not found") - use normalized key so URL "asda" matches stored "ASDA"
      if (roomToReconnect && failedRoomsRef?.current?.has(roomToReconnect)) {
        console.log('Skipping reconnection to recently failed room:', roomToReconnect);
        return;
      }
      // After 3 "room not found" attempts, stop reconnecting and don't emit join/get-room-state again
      if (roomNotFoundAttemptsRef.current >= ROOM_NOT_FOUND_MAX_ATTEMPTS) {
        console.log('Room not found max attempts reached, skipping reconnection');
        return;
      }
      
      // Max 3 reconnection join/get-room-state attempts - no unlimited retries
      if (reconnectEmitCountRef.current >= ROOM_NOT_FOUND_MAX_ATTEMPTS) {
        console.log('Reconnection emit cap reached (3), skipping');
        return;
      }

      // Reconnect if we have a room to rejoin: either player (playerName) or host (hostToken).
      // Use hostToken as the source of truth for host reconnection, not sessionStorage isHost -
      // sessionStorage is shared across tabs, so the player tab can overwrite isHost to false.
      if (roomToReconnect && (savedData.playerName || savedData.hostToken)) {
        // Clear saved room identity if using URL code that doesn't match saved
        if (roomCodeFromUrl && savedData.roomName && formatRoomCode(roomCodeFromUrl) !== (savedData.roomName ? formatRoomCode(savedData.roomName) : '')) {
          console.log(`Clearing saved room data: URL code (${roomCodeFromUrl}) doesn't match saved (${savedData.roomName})`);
          RoomSessionStorage.clearRoomSessionIdentity();
        }
        
        setTimeout(() => {
          if (reconnectEmitCountRef.current >= ROOM_NOT_FOUND_MAX_ATTEMPTS) return;
          const currentRoomState = roomStateRef.current;
          const skip = shouldSkipReconnectEmit({
            needsRejoin: needsRejoinRef.current,
            savedRoomName: roomToReconnect,
            currentRoomName: currentRoomState?.roomName,
          });
          if (!skip) {
            reconnectEmitCountRef.current += 1;
            const latest = RoomSessionStorage.getSavedRoomData();
            const isHostReconnect = latest.roomName === roomToReconnect && !!latest.hostToken;
            const hostPlayToo = isHostReconnect && !!latest.playerName;
            console.log(
              'Reconnecting to room:',
              roomToReconnect,
              'asHost:',
              isHostReconnect,
              'hostPlayToo:',
              hostPlayToo,
              'fromURL:',
              !!roomCodeFromUrl,
              needsRejoinRef.current ? 'needsRejoin' : '',
              `(emit ${reconnectEmitCountRef.current}/${ROOM_NOT_FOUND_MAX_ATTEMPTS})`
            );
            emitRoomRestore(socket, latest, { roomName: roomToReconnect });
          } else if (!options?.silent) {
            console.log('Already in room with state, skipping duplicate state request');
          }
        }, 200);
      }
    };

    const handleConnectError = (error: Error) => {
      console.log('Connection error:', error.message);
      if (isLeavingRef.current) {
        console.log('Connection error but user intentionally left - keeping connected=true');
        setConnected(true);
        return;
      }
      setConnected(false);
      
      // Track connection error attempts to detect when server is unreachable
      connectionErrorAttemptsRef.current += 1;
      const maxAttempts = 3; // Show error after 3 failed attempts
      
      // Check if error indicates server is unreachable
      const isUnreachable = error.message.includes('ERR_CONNECTION_REFUSED') || 
                           error.message.includes('xhr poll error') ||
                           error.message.includes('ECONNREFUSED') ||
                           error.message.includes('NetworkError');
      
      if (isUnreachable && connectionErrorAttemptsRef.current >= maxAttempts) {
        setConnectionError('Server is unreachable. Please check if the backend server is running.');
      } else if (connectionErrorAttemptsRef.current >= maxAttempts * 2) {
        // After even more attempts, show generic error
        setConnectionError('Unable to connect to the server after multiple attempts. The server may be down.');
      }
    };

    const handleDisconnect = () => {
      console.log('Disconnected from backend');

      if (isLeavingRef.current || suppressSessionRestoreRef?.current) {
        console.log('Disconnected but user intentionally left - keeping connected=true and skipping reconnection');
        setConnected(true);
        return;
      }

      // Spec: reconnect whenever session still has a room — even if roomState is still null
      // (drop after join/restore before first room-state is common on flaky mobile).
      const savedRoomName = RoomSessionStorage.getRoomName();
      if (!savedRoomName) {
        console.log('Disconnected but no room in session storage - showing start screen');
        setConnected(true);
        setRoomState(null);
        setShowJoinRoom(false);
        return;
      }

      setConnected(false);
      // Lobby removes players on disconnect; local roomState is often kept for UX.
      // Mark membership stale so reconnect always re-emits join-room / get-room-state.
      needsRejoinRef.current = true;
      startReconnection();
    };

    // ========== ROOM EVENTS ==========

    const handleRoomCreated = (data: { roomCode: string; gameType: string; hostToken?: string }) => {
      console.log('Room created:', data);
      // Leave confirmed while create-room was in flight — tear down orphan, stay on setup
      if (isLeavingRef.current || suppressSessionRestoreRef?.current) {
        console.log('Ignoring room-created - user intentionally left');
        if (data.roomCode) {
          socket.emit('leave-room', { roomName: data.roomCode });
        }
        onHostDeepLinkSettled?.();
        return;
      }
      // Only creator receives this event (host device); play SFX
      playRoomCreated();
      roomNotFoundToastShownRef.current = false;
      roomNotFoundAttemptsRef.current = 0;
      reconnectEmitCountRef.current = 0;
      if (data.roomCode) {
        failedRoomsRef?.current?.delete(data.roomCode);
        RoomSessionStorage.setRoomName(data.roomCode);
        RoomSessionStorage.setIsHost(true);
        if (data.hostToken) {
          RoomSessionStorage.setHostToken(data.hostToken);
        }

        // Clear host parameter from URL so refresh shows root (avoids stale ?host= when room is gone)
        if (getHostCodeFromUrl() === data.roomCode) {
          clearQueryParamFromUrl('host');
          // Keep creatingHostFromUrl true until room-state arrives (avoids setup flash between
          // room-created and room-state). Failure paths call onHostDeepLinkSettled.
        }

        // Clear /host entry path so refresh does not auto-create another room
        clearEntryRouteFromUrl();

        socket.emit('get-room-state', { roomName: data.roomCode });
        setIsHost(true);

        if (pendingStartGameRef.current) {
          setTimeout(() => {
            const hostToken = data.hostToken ?? RoomSessionStorage.getSavedRoomData()?.hostToken ?? undefined;
            socket.emit('start-game', { roomName: data.roomCode, hostToken });
            pendingStartGameRef.current = null;
          }, 200);
        }
      }
    };

    const handleRoomJoined = (data: { roomName: string; playerName: string; avatar?: string; playerId?: string }) => {
      console.log('Room joined:', data);
      if (isHostRef.current) playRoomJoined();
      roomNotFoundToastShownRef.current = false;
      roomNotFoundAttemptsRef.current = 0;
      reconnectEmitCountRef.current = 0;
      needsRejoinRef.current = false;
      if (data.roomName) {
        failedRoomsRef?.current?.delete(data.roomName);
        RoomSessionStorage.setRoomName(data.roomName);
        RoomSessionStorage.setPlayerName(data.playerName);
        // Host "play too" also gets room-joined. Keep host credentials when hostToken is present
        // so visibility/reconnect emits still attach hostToken (session isHost is not SoT).
        if (!RoomSessionStorage.hasHostToken()) {
          RoomSessionStorage.setIsHost(false);
          setIsHost(false);
        }
        if (data.avatar) {
          RoomSessionStorage.setAvatar(data.avatar);
        }

        // Prefer stable backend player ID. Fall back to socket ID for backward compatibility.
        const resolvedPlayerId = data.playerId || socket.id || null;
        setMyPlayerId(resolvedPlayerId);
        if (resolvedPlayerId) {
          console.log(`Player rejoined - updated myPlayerId to: ${resolvedPlayerId}`);
        }

        setShowJoinRoom(false);

        clearEntryRouteFromUrl();

        setTimeout(() => {
          const hostToken = RoomSessionStorage.getHostToken() || undefined;
          socket.emit('get-room-state', { roomName: data.roomName, hostToken });
        }, 100);

        // Notify backend that we (a player) joined/rejoined so host gets updated player list
        // Critical for reconnection: when player reconnects after phone lock, host may not have
        // received the room-state broadcast; player-back-online triggers a second broadcast.
        // Also emit for host play-too (has hostToken + playerName).
        if (data.playerName) {
          setTimeout(() => {
            socket.emit('player-back-online', { roomName: data.roomName });
          }, 150);
        }
      }
    };

    const handleGameTypeChanged = (data: { roomName: string; gameType: string }) => {
      console.log('Game type changed:', data);
      if (data.roomName) {
        socket.emit('get-room-state', { roomName: data.roomName });
        // Clear last attempted game type since backend accepted it
        // We need to access the ref from App.tsx - this will be handled by room-state update
      }
    };

    const handleVotesUpdated = (data: { roomName: string; votes: Record<string, number> }) => {
      console.log('Votes updated:', data);
      updateRoomStateForRoom(data.roomName, (prev) => ({
        ...prev,
        votes: data.votes,
      }));
    };

    const handleCategoryVotesUpdated = (data: {
      roomName: string;
      categoryVotes: Record<string, number>;
    }) => {
      console.log('Category votes updated:', data);
      updateRoomStateForRoom(data.roomName, (prev) => ({
        ...prev,
        categoryVotes: data.categoryVotes,
      }));
    };

    const handlePlayerJoined = (data: any) => {
      console.log('Player joined:', data);
      if (isHostRef.current) playPlayerJoined();
      if (data.roomName && data.players) {
        updateRoomStateForRoom(data.roomName, (prev) => ({
          ...prev,
          players: data.players,
        }));
        socket.emit('get-room-state', { roomName: data.roomName });
      }
    };

    const handlePlayerLeft = (data: any) => {
      console.log('Player left:', data);
      if (data.roomName && data.players) {
        updateRoomStateForRoom(data.roomName, (prev) => ({
          ...prev,
          players: data.players,
        }));
        socket.emit('get-room-state', { roomName: data.roomName });
      }
    };

    const handleRoomState = (state: RoomState) => {
      const savedRoomName = RoomSessionStorage.getRoomName();
      const savedHostToken = RoomSessionStorage.getHostToken();
      const isReturnToWaiting =
        state.gameState === 'waiting' &&
        (savedRoomName === state.roomName || roomStateRef.current?.roomName === state.roomName);
      const isHostSessionRestore =
        !!savedHostToken &&
        savedRoomName === state.roomName &&
        !roomStateRef.current;

      // Intentional leave must win over host-session-restore (race: get-room-state before destroy)
      if (suppressSessionRestoreRef?.current) {
        console.log('Ignoring room-state event - session restore suppressed after intentional leave');
        return;
      }

      if (isLeavingRef.current && !isReturnToWaiting && !isHostSessionRestore) {
        console.log('Ignoring room-state event - user is leaving');
        return;
      }
      if (isLeavingRef.current && (isReturnToWaiting || isHostSessionRestore)) {
        isLeavingRef.current = false;
      }

      // Check if the room we're trying to reconnect to is expired
      const savedData = RoomSessionStorage.getSavedRoomData();
      const isExpired = savedData.expiresAt ? Date.now() > savedData.expiresAt : false;
      if (isExpired && savedData.roomName === state.roomName) {
        console.log(`Ignoring room-state for expired room: ${state.roomName} expired at ${new Date(savedData.expiresAt!).toISOString()}`);
        RoomSessionStorage.clearAll();
        setRoomState(null);
        setShowJoinRoom(false); // Show room setup page
        setRestoreAttemptSettled?.(true);
        return;
      }

      const savedIsHost = RoomSessionStorage.getIsHost();
      
      // If we don't have a saved room name, but we're receiving room-state, it might be:
      // 1. Premium room creation via URL (room-state arrives before room-created)
      // 2. Reconnection scenario where session storage was cleared
      // In these cases, if socket.id matches hostId AND we're not currently in join flow, we should accept it
      if (!savedRoomName) {
        // Check if we're the host (socket.id matches hostId) - this indicates we just created the room
        const isHostBySocketId = socket.id && state.hostId && socket.id === state.hostId;
        // CRITICAL: Only set as host if we're NOT in the join room flow
        // Use ref — showJoinRoom is not in effect deps and would be stale in the closure
        const isInJoinFlow = showJoinRoomRef.current;
        if (isHostBySocketId && !isInJoinFlow) {
          // We're the host but don't have room saved - this is likely a premium room creation
          // Save the room name and host status now
          console.log(`Accepting room-state for new room ${state.roomName} (host detected, not in join flow)`);
          RoomSessionStorage.setRoomName(state.roomName);
          RoomSessionStorage.setIsHost(true);
        } else {
          // Not the host and no saved room - ignore it (might be a stale event)
          // OR we're in join flow, so don't set as host even if socket.id matches
          if (isHostBySocketId && isInJoinFlow) {
            console.log('Ignoring host detection - currently in join room flow, joining as player');
          } else {
            console.log('Ignoring room-state event - no saved room in session and not the host');
          }
          return;
        }
      } else if (formatRoomCode(savedRoomName) !== formatRoomCode(state.roomName)) {
        // We have a saved room but it doesn't match - ignore it
        console.log(
          `Ignoring room-state event - wrong room (saved: ${savedRoomName}, received: ${state.roomName})`
        );
        return;
      }

      const currentRoomState = roomStateRef.current;
      if (!currentRoomState || currentRoomState.roomName === state.roomName) {
        // Keep a stable identity across reconnects by deriving the local player id from room-state.
        // Only set when exactly one player matches by name to avoid wrong identity with duplicate names.
        const localPlayerName = RoomSessionStorage.getPlayerName();
        if (localPlayerName && Array.isArray(state.players)) {
          const matches = state.players.filter((p: any) => p?.name === localPlayerName);
          if (matches.length === 1 && matches[0]?.id) {
            setMyPlayerId(matches[0].id);
          }
        }

        console.log(`📊 Room-state received for room ${state.roomName}: ${state.players?.length || 0} players`);
        if (state.players && state.players.length > 5) {
          console.warn(`⚠️ Room-state has more than 5 players (${state.players.length}). All should be displayed.`);
        }
        
        // Check for host reconnection (host was disconnected but is now back)
        // hostDisconnectedAt is null/undefined when host is connected
        const currentHostDisconnectedAt = (state as any).hostDisconnectedAt;
        const previousHostDisconnectedAt = previousHostDisconnectedAtRef.current;
        
        // Normalize current value (undefined -> null for consistency)
        const normalizedCurrent = currentHostDisconnectedAt === undefined ? null : currentHostDisconnectedAt;
        
        // If host is disconnected (currentHostDisconnectedAt is a number/timestamp),
        // update our tracking ref so we can detect reconnection later
        if (!isHostRef.current && typeof normalizedCurrent === 'number') {
          // Host is currently disconnected - update ref if not already set
          if (previousHostDisconnectedAt === null || previousHostDisconnectedAt === undefined) {
            previousHostDisconnectedAtRef.current = normalizedCurrent;
            console.log('🔍 Detected host disconnection from room-state, set previousHostDisconnectedAtRef to:', normalizedCurrent);
          }
        }
        
        // Debug logging for host reconnection detection
        if (!isHostRef.current && previousHostDisconnectedAt !== undefined && previousHostDisconnectedAt !== null) {
          console.log('🔍 Host reconnection check:', {
            previous: previousHostDisconnectedAt,
            current: normalizedCurrent,
            previousType: typeof previousHostDisconnectedAt,
            currentType: typeof normalizedCurrent,
            wasDisconnected: typeof previousHostDisconnectedAt === 'number',
            isNowConnected: normalizedCurrent === null
          });
        }
        
        // Host reconnected if:
        // 1. Previous state had hostDisconnectedAt set (host was disconnected) - must be a number (timestamp)
        // 2. Current state has hostDisconnectedAt as null (host is now connected)
        // 3. We're not the host (only show to players)
        // 4. HostId matches (host is actually in the same room)
        // IMPORTANT: Only show "host reconnected" if host is actually in THIS room
        // If host moved to a different room, the host-moved-to-new-room event will handle it
        if (
          !isHostRef.current && 
          previousHostDisconnectedAt !== undefined &&
          previousHostDisconnectedAt !== null &&
          typeof previousHostDisconnectedAt === 'number' &&
          normalizedCurrent === null &&
          state.hostId && // Host ID exists
          savedRoomName &&
          formatRoomCode(state.roomName) === formatRoomCode(savedRoomName) // Host is in the same room we're in
        ) {
          showToast(t('toasts.hostReconnected'), 'success', 5000);
          console.log('✅ Host reconnected - showing toast to players', {
            previous: previousHostDisconnectedAt,
            current: normalizedCurrent,
            hostId: state.hostId,
            roomName: state.roomName
          });
        }
        
        // Update previous state to track changes
        if (previousHostDisconnectedAt !== normalizedCurrent) {
          previousHostDisconnectedAtRef.current = normalizedCurrent;
        }

        if (currentRoomState && currentRoomState.players && state.players) {
          state.players.forEach((player: any) => {
            const prevPlayer = currentRoomState.players.find((p: any) => p.id === player.id);
            if (prevPlayer && player.score !== prevPlayer.score) {
              console.log(
                `📊 Score update received: ${player.name} - Old: ${prevPlayer.score}, New: ${player.score}, Diff: ${player.score - prevPlayer.score}`
              );
            }
          });
        }

        // When game is finished, preserve finalLeaderboard/gameStatistics from previous state
        // if server didn't send them (server room-state doesn't include these; we got them from game-ended).
        const feInBonus =
          !!currentRoomState?.isBonusLevel || !!currentRoomState?.bonusLevelResult;
        const bonusAnchor = currentRoomState?.bonusLevelPayload?.questionNumber;
        const incomingQuestionNum = state.currentQuestion?.questionNumber;
        const clearlyNextAfterBonus =
          typeof bonusAnchor === 'number' &&
          typeof incomingQuestionNum === 'number' &&
          incomingQuestionNum > bonusAnchor;
        // Hold bonus UI when server briefly clears isBonusLevel (outro / DO wake / focus
        // get-room-state) without a clearly-next main question — prevents host jumping to
        // the pre-bonus question while the player is still mid-bonus.
        const shouldHoldBonusScreen =
          feInBonus &&
          currentRoomState.gameState === 'playing' &&
          state.gameState === 'playing' &&
          !state.isBonusLevel &&
          !clearlyNextAfterBonus;

        const mergedState: RoomState = shouldHoldBonusScreen
          ? {
              ...state,
              // Hold the bonus UI until the next real question arrives so the host
              // doesn't flash the generic loading fallback between states.
              isBonusLevel: true,
              bonusLevelPayload:
                currentRoomState?.bonusLevelPayload ?? state.bonusLevelPayload,
              bonusStartTime: currentRoomState?.bonusStartTime ?? state.bonusStartTime,
              bonusLevelResult: currentRoomState?.bonusLevelResult,
              // Keep FE off the stale pre-bonus question while we hold the bonus screen.
              currentQuestion: clearlyNextAfterBonus
                ? state.currentQuestion
                : currentRoomState?.currentQuestion ?? null,
            }
          : { ...state };
        if (state.gameState === 'finished' && currentRoomState) {
          if (currentRoomState.finalLeaderboard != null) mergedState.finalLeaderboard = currentRoomState.finalLeaderboard;
          if (currentRoomState.gameStatistics != null) mergedState.gameStatistics = currentRoomState.gameStatistics;
        }
        // bonus-level-result is FE-only; room-state broadcasts can race and wipe it before the host
        // applies results. Never preserve it once the next main question has clearly arrived —
        // that stuck casting hosts on BonusHostScreen while players advanced.
        if (
          currentRoomState?.bonusLevelResult &&
          !state.bonusLevelResult &&
          !clearlyNextAfterBonus &&
          (shouldHoldBonusScreen || state.isBonusLevel || !state.currentQuestion)
        ) {
          mergedState.bonusLevelResult = currentRoomState.bonusLevelResult;
        }
        if (mergedState.players?.length) {
          // Play Again / new game: BE resets scores to 0. Do not keep previous-game totals.
          const startingFreshGame =
            state.gameState === 'playing' &&
            (currentRoomState?.gameState === 'finished' ||
              currentRoomState?.gameState === 'waiting');
          mergedState.players = mergePlayersWithoutScoreRegression(
            mergedState.players,
            currentRoomState?.players,
            { allowRegression: startingFreshGame }
          );
        }
        // Preserve company questions / AI usage if a brief room-state omit races a generate.
        if (
          state.companyQuestions === undefined &&
          currentRoomState?.companyQuestions?.length
        ) {
          mergedState.companyQuestions = currentRoomState.companyQuestions;
        }
        // Public room-state strips correctAnswer; keep host-full bank when we already have it.
        if (
          state.companyQuestions?.length &&
          currentRoomState?.companyQuestions?.length &&
          currentRoomState.companyQuestions.some((q) => !!q.correctAnswer) &&
          !state.companyQuestions.some((q) => !!q.correctAnswer)
        ) {
          mergedState.companyQuestions = currentRoomState.companyQuestions;
        }
        if (state.aiPromptUsage === undefined && currentRoomState?.aiPromptUsage) {
          mergedState.aiPromptUsage = currentRoomState.aiPromptUsage;
        }
        // Preserve question index when a room-state refresh omits them (common after DO restore / bonus transition).
        if (mergedState.currentQuestion) {
          const nextQ = mergedState.currentQuestion;
          const prevQ = currentRoomState?.currentQuestion;
          const prevId = prevQ?.questionId ?? prevQ?.id;
          const nextId = nextQ.questionId ?? nextQ.id;
          const bonusAnchor = currentRoomState?.bonusLevelPayload?.questionNumber;
          const bonusTotal = currentRoomState?.bonusLevelPayload?.totalQuestions;
          let qNum =
            typeof nextQ.questionNumber === 'number'
              ? nextQ.questionNumber
              : prevId && nextId && prevId === nextId
                ? prevQ?.questionNumber
                : undefined;
          let qTotal =
            typeof nextQ.totalQuestions === 'number'
              ? nextQ.totalQuestions
              : prevId && nextId && prevId === nextId
                ? prevQ?.totalQuestions
                : undefined;
          // After bonus outro, server may send room-state before question event; infer next index from bonus anchor.
          if (typeof qNum !== 'number' && typeof bonusAnchor === 'number' && !currentRoomState?.isBonusLevel) {
            qNum = bonusAnchor + 1;
          }
          if (typeof qTotal !== 'number' && typeof bonusTotal === 'number') {
            qTotal = bonusTotal;
          }
          if (typeof qNum === 'number' || typeof qTotal === 'number') {
            mergedState.currentQuestion = {
              ...nextQ,
              ...(typeof qNum === 'number' ? { questionNumber: qNum } : {}),
              ...(typeof qTotal === 'number' ? { totalQuestions: qTotal } : {}),
            };
          }
        }
        // Puzzle bonus: server doesn't store imageUrl; preserve it when room-state overwrites so host/players keep the image.
        if (
          state.isBonusLevel &&
          state.bonusLevelPayload &&
          currentRoomState?.bonusLevelPayload?.imageUrl &&
          state.bonusLevelPayload.questionId === currentRoomState.bonusLevelPayload.questionId &&
          !state.bonusLevelPayload.imageUrl
        ) {
          mergedState.bonusLevelPayload = { ...state.bonusLevelPayload, imageUrl: currentRoomState.bonusLevelPayload.imageUrl };
        }

        // Re-check after merge work — leave may have started while this handler was running
        if (suppressSessionRestoreRef?.current) {
          console.log('Ignoring room-state apply - session restore suppressed after intentional leave');
          return;
        }

        setRoomState(mergedState);
        setRestoreAttemptSettled?.(true);

        // Soft-reconnect: clear needsRejoin once server confirms our membership
        // (player connected in list, or host restored via hostToken).
        const playerConnected = isLocalPlayerConnectedInState(state.players, localPlayerName);
        const hostRestored =
          !!savedHostToken &&
          !!socket.id &&
          !!state.hostId &&
          socket.id === state.hostId;
        if (playerConnected || hostRestored) {
          needsRejoinRef.current = false;
        } else if (
          !isLeavingRef.current &&
          state.gameState === 'waiting' &&
          localPlayerName &&
          Array.isArray(state.players)
        ) {
          // Lobby dropped us (phone lock) but we still have session — rejoin so host/peers see us.
          const now = Date.now();
          if (now - lastAutoRejoinAtRef.current >= AUTO_REJOIN_THROTTLE_MS) {
            lastAutoRejoinAtRef.current = now;
            needsRejoinRef.current = true;
            const saved = RoomSessionStorage.getSavedRoomData();
            console.log('Lobby room-state missing local player — emitting join-room to rejoin');
            socket.emit('join-room', {
              roomName: state.roomName,
              playerName: localPlayerName,
              avatar: saved.avatar || undefined,
            });
          }
        }

        // Store expiration date if this is a premium room with a known expiry.
        // Admin/premium-without-expiry must clear any stale expiresAt from a prior paid room
        // so leave/preserve logic cannot treat TOYO/GAME/ROOM as an expiring purchase.
        if (state.isPremium && (state as any).expiresAt) {
          const nextExpiresAt = (state as any).expiresAt as number;
          const prevExpiresAt = RoomSessionStorage.getSavedRoomData().expiresAt;
          RoomSessionStorage.setRoomExpiresAt(nextExpiresAt);
          if (prevExpiresAt !== nextExpiresAt) {
            console.log(`💾 Stored room expiration: ${new Date(nextExpiresAt).toISOString()}`);
          }
        } else {
          RoomSessionStorage.clearRoomExpiresAt();
        }

        // Server is the single source of truth for this tab's host role (UI only).
        // Derive isHost from socket.id === state.hostId so this tab shows correct role.
        // Do NOT write to RoomSessionStorage here: sessionStorage is shared across tabs. If a
        // player tab receives room-state and we set isHost false / clearHostData(), we would
        // wipe the real host's token in another tab and break host reconnection/refresh.
        const isCurrentHost = socket.id && state.hostId && socket.id === state.hostId;
        setIsHost(!!isCurrentHost);
        if (savedIsHost && !isCurrentHost && socket.id && state.hostId) {
          console.log('Host status corrected from room-state: we are not the host (socket.id !== hostId)');
        }
      }
    };

    const handleRoomError = (error: { message: string; isPremiumRoom?: boolean; roomName?: string; reconnectTo?: string }) => {
      console.error('Room error:', error);

      // Production (Cloudflare): backend says we're on wrong Durable Object - reconnect to correct DO and retry
      if (error.reconnectTo && onReconnectTo) {
        console.log('Reconnecting to correct DO for room:', error.reconnectTo);
        onReconnectTo(error.reconnectTo);
        return;
      }

      // "Already in room" on reconnection (e.g. refresh while waiting for host): don't show toast; we're already in the right place.
      if (error.message && error.message.includes('Already in room')) {
        needsRejoinRef.current = false;
        const savedData = RoomSessionStorage.getSavedRoomData();
        if (savedData.roomName && !roomStateRef.current?.roomName) {
          socket.emit('get-room-state', { roomName: savedData.roomName });
        }
        return;
      }

      // Check if this is an admin/premium room already in use or already hosted error
      const isAdminRoomInUse = error.message && (
        error.message.includes('already in use') ||
        error.message.includes('Room code already in use') ||
        error.message.includes('Room already hosted by someone else')
      );
      
      // Check if this is an admin room code (TOYO, GAME, ROOM)
      const hostCodeFromUrl = getHostCodeFromUrl();
      const isAdminRoom = hostCodeFromUrl && ['TOYO', 'GAME', 'ROOM'].includes(hostCodeFromUrl.toUpperCase());
      
      // Only redirect to home for specific errors that indicate the room was destroyed or doesn't exist
      // Don't redirect for validation errors (like "room code not available", "not available", etc.) - keep user on join screen
      const isValidationError = error.message && (
        error.message.includes('not available') ||
        error.message.includes('is not available') ||
        error.message.includes('Invalid room code') ||
        error.message.includes('Room code') && error.message.includes('not available') ||
        isAdminRoomInUse // Don't redirect for admin room in use - let user try another
      );

      const isRoomNotFound = error.message && (
        error.message.includes('Room not found') ||
        error.message.includes('room not found') ||
        /Room\s+"[^"]+"\s+not found/i.test(error.message) ||
        /Room\s+'[^']+'\s+not found/i.test(error.message) ||
        error.message.includes('Room destroyed') ||
        error.message.includes('room was closed')
      ) && !isValidationError;

      if (isRoomNotFound) {
        // User intentionally went back/home - don't show toast or re-run cleanup (already done in handleGoHome)
        if (isLeavingRef.current || suppressSessionRestoreRef?.current) {
          onHostDeepLinkSettled?.();
          return;
        }
        // Add to failed rooms (normalized) so any reconnection/connect handler skips this room immediately
        const failedRoomRaw =
          error.roomName ||
          RoomSessionStorage.getSavedRoomData().roomName ||
          error.message?.match(/Room\s+"([A-Za-z0-9]+)"\s+not found/i)?.[1] ||
          error.message?.match(/Room\s+'([A-Za-z0-9]+)'\s+not found/i)?.[1];
        const failedRoom = failedRoomRaw ? formatRoomCode(failedRoomRaw) : null;
        if (failedRoom && failedRoomsRef?.current) {
          failedRoomsRef.current.add(failedRoom);
        }
        clearReconnection();
        setReconnecting(false);
        setReconnectAttempts(0);
        // Cap retries so reconnection does not spam join-room for this code
        roomNotFoundAttemptsRef.current += 1;
        reconnectEmitCountRef.current = ROOM_NOT_FOUND_MAX_ATTEMPTS; // cap so no more reconnection emits
        const attempts = roomNotFoundAttemptsRef.current;
        if (attempts >= ROOM_NOT_FOUND_MAX_ATTEMPTS) {
          console.log('Room not found: max attempts reached, redirecting to game setup (no more reconnection)');
        } else {
          console.log(`Room not found (attempt ${attempts}/${ROOM_NOT_FOUND_MAX_ATTEMPTS}), redirecting to game setup`);
        }
        setRoomState(null);
        setIsHost(false);
        const wasOnJoinScreen = showJoinRoomRef.current || isJoinEntryPath();
        const implicitHostCreate = isImplicitHostCreatePath();
        if (wasOnJoinScreen) {
          // Stay on JoinRoom and keep roomName in session so typed code survives any remount.
          // failedRooms + emit cap already stop auto-retries.
          RoomSessionStorage.clearHostData();
          RoomSessionStorage.clearRoomExpiresAt();
          setShowJoinRoom(true);
        } else {
          RoomSessionStorage.clearRoomKeepPlayer();
          setShowJoinRoom(false);
          clearQueryParamFromUrl('room');
          clearQueryParamFromUrl('host');
          if (implicitHostCreate) {
            reconnectEmitCountRef.current = 0;
            roomNotFoundAttemptsRef.current = 0;
            setRestoreAttemptSettled?.(true);
            onScheduleCreateRoom?.({
              gameType: config.games[0]?.id || 'trivia',
              roomCode: generateRoomCode(),
            });
          } else {
            clearEntryRouteFromUrl();
            onHostDeepLinkSettled?.();
          }
        }
        setRestoreAttemptSettled?.(true);
        if (wasOnJoinScreen) {
          dispatchJoinRoomError(JOIN_ROOM_NOT_FOUND_MESSAGE());
        } else if (!implicitHostCreate && !roomNotFoundToastShownRef.current) {
          roomNotFoundToastShownRef.current = true;
          showToast(t('toasts.roomNotExist'), 'warning');
        }
        return;
      }

      // Show appropriate error message for non-"room not found" errors (never show same toast 3 times)
      const isDuplicateName = error.message && (
        error.message.includes('name') &&
        (error.message.includes('already') || error.message.includes('choose another name'))
      );
      const isHostAlreadyUsing = error.message && (
        error.message.includes('already in use') ||
        error.message.includes('already hosted') ||
        error.message.includes('already in use by another host')
      );
      const messageToShow = isDuplicateName
        ? t('toasts.duplicatePlayerName')
        : isHostAlreadyUsing
          ? t('toasts.hostAlreadyUsingRoom')
          : isAdminRoomInUse && isAdminRoom
            ? t('toasts.adminRoomInUse')
            : translateBackendError(error.message);
      const now = Date.now();
      const last = lastRoomErrorToastRef.current;
      const skipToast = last.message === messageToShow && now - last.at < ROOM_ERROR_TOAST_DEBOUNCE_MS;
      if (!skipToast) {
        lastRoomErrorToastRef.current = { message: messageToShow, at: now };
        showToast(messageToShow, 'error');
      }

      // /host auto-create failed — return to root hub instead of infinite loader
      if (isHostEntryPath() && !hostCodeFromUrl && !getRoomCodeFromUrl()) {
        clearEntryRouteFromUrl();
        navigateToRoot(true);
        setShowJoinRoom(false);
        setRestoreAttemptSettled?.(true);
        onHostDeepLinkSettled?.();
      }

      // If we had ?host= and create-room failed (e.g. host already using), clear URL and mark failed so we don't retry
      if (hostCodeFromUrl && failedRoomsRef?.current) {
        failedRoomsRef.current.add(hostCodeFromUrl);
        clearQueryParamFromUrl('host');
        onHostDeepLinkSettled?.();
      }
    };

    const handlePremiumRoomNotActivated = (data: { roomName: string; canCreate: boolean }) => {
      console.log('Premium room not activated, can create:', data);
      
      if (data.canCreate && data.roomName) {
        // Room is available and can be created - automatically navigate to host creation URL
        console.log(`Premium room ${data.roomName} is available - redirecting to create as host`);
        window.location.href = `/?host=${data.roomName}`;
      }
    };

    const handleRoomDestroyed = (data: { roomName: string; reason: string }) => {
      console.log('Room destroyed:', data);

      const userInitiatedLeave = isLeavingRef.current;
      
      // Clear reconnection state immediately - room is gone, no point reconnecting
      clearReconnection();
      setReconnecting(false);
      setReconnectAttempts(0);
      needsRejoinRef.current = false;
      
      // For premium rooms (with expiration), preserve hostToken when room is destroyed so host can rejoin.
      // Do NOT preserve for admin rooms (TOYO, GAME, ROOM) - they have no expiresAt, and preserving
      // causes get-room-state spam and stale reconnection attempts until host creates a new room.
      const savedData = RoomSessionStorage.getSavedRoomData();
      const hasPremiumExpiration = savedData.expiresAt != null && Date.now() < savedData.expiresAt;
      const isPremiumRoom = hasPremiumExpiration && savedData.hostToken && savedData.roomName === data.roomName;
      const savedHostToken = isPremiumRoom ? savedData.hostToken : null;
      const savedRoomName = isPremiumRoom ? savedData.roomName : null;

      // room-not-found: clear room/host only, keep player name and avatar for next join
      if (data.reason === 'room-not-found') {
        RoomSessionStorage.clearRoomKeepPlayer();
      } else {
        RoomSessionStorage.clearAll();
        // Restore hostToken and roomName only for premium (expiring) rooms to allow reconnection
        if (isPremiumRoom && savedHostToken && savedRoomName) {
          console.log(`Preserving hostToken for premium room ${savedRoomName} after room destruction to allow reconnection`);
          RoomSessionStorage.setRoomName(savedRoomName);
          RoomSessionStorage.setHostToken(savedHostToken);
          RoomSessionStorage.setIsHost(true);
        }
      }
      
      setRoomState(null);
      setIsHost(false);
      pendingStartGameRef.current = null;
      
      // Immediately settle the restore attempt so the loading shell disappears and setup tiles show
      setRestoreAttemptSettled?.(true);
      
      // Reset connection state - we're back to start screen, mark as connected
      // This prevents showing "connecting to server" when socket is disconnected
      setConnected(true);

      // Only go to start screen when room is destroyed if user didn't initiate leave
      const wasOnJoinScreen = showJoinRoomRef.current || isJoinEntryPath();
      if (!userInitiatedLeave && !wasOnJoinScreen) {
        setShowJoinRoom(false);
      } else if (!userInitiatedLeave && wasOnJoinScreen) {
        setShowJoinRoom(true);
        navigateToJoin({ replace: true });
      }

      // Reset the host disconnected toast flag when room is destroyed
      hostDisconnectedToastShownRef.current = false;
      
      if (data.reason === 'host-left') {
        // Only show toast to players, not to the host who intentionally left
        // If we already showed "waiting for reconnection", don't show this one
        // (the waiting toast is more informative)
        // Also don't show if user initiated the leave (they're navigating intentionally)
        if (!isHostRef.current && !hostDisconnectedToastShownRef.current && !userInitiatedLeave) {
          showToast(t('toasts.hostLeftRoom'), 'warning');
        }
      } else if (data.reason === 'no-players') {
        console.log('Room closed: no players remaining');
      } else if (data.reason === 'room-not-found') {
        const failedRoom = data.roomName ? formatRoomCode(data.roomName) : null;
        if (failedRoom && failedRoomsRef?.current) {
          failedRoomsRef.current.add(failedRoom);
        }
        roomNotFoundAttemptsRef.current += 1;
        if (!userInitiatedLeave) {
          if (wasOnJoinScreen) {
            dispatchJoinRoomError(JOIN_ROOM_NOT_FOUND_MESSAGE());
          } else if (!roomNotFoundToastShownRef.current && !isImplicitHostCreatePath()) {
            roomNotFoundToastShownRef.current = true;
            showToast(t('toasts.roomNotExist'), 'warning');
          }
        }
        if (!userInitiatedLeave && isImplicitHostCreatePath()) {
          onScheduleCreateRoom?.({
            gameType: config.games[0]?.id || 'trivia',
            roomCode: generateRoomCode(),
          });
        }
        console.log('Room not found - room may have been closed');
      } else if (data.reason === 'admin-kill') {
        showToast(t('toasts.roomClosedByAdmin'), 'warning');
        console.log('Room closed by admin');
      } else if (data.reason === 'lobby-idle-timeout') {
        if (!userInitiatedLeave) {
          showToast(t('toasts.lobbyIdleClosed'), 'warning');
        }
        console.log('Room closed due to lobby idle timeout');
      } else if (data.reason === 'finished-idle-timeout') {
        if (!userInitiatedLeave) {
          showToast(t('toasts.finishedIdleClosed'), 'warning');
        }
        console.log('Room closed due to finished idle timeout');
      } else if (data.reason === 'stale-playing-timeout') {
        if (!userInitiatedLeave) {
          showToast(t('toasts.stalePlayingClosed'), 'warning');
        }
        console.log('Room closed due to stale playing timeout');
      }

      isLeavingRef.current = false;
    };

    const handleHostDisconnected = (data: { roomName: string; message: string }) => {
      console.log('Host disconnected:', data);
      const current = roomStateRef.current;
      if (!isHostRef.current && current && current.roomName === data.roomName) {
        // Only show toast if we haven't shown it yet
        if (!hostDisconnectedToastShownRef.current) {
          showToast(t('toasts.hostLeftWaiting'), 'warning');
          hostDisconnectedToastShownRef.current = true;
        }
        // Mark that host is disconnected by setting previousHostDisconnectedAtRef to a timestamp
        // This ensures we can detect when host reconnects (when it becomes null in room-state)
        // Use a timestamp to indicate host was disconnected
        if (previousHostDisconnectedAtRef.current === null || previousHostDisconnectedAtRef.current === undefined) {
          previousHostDisconnectedAtRef.current = Date.now();
          console.log('🔍 Marked host as disconnected in previousHostDisconnectedAtRef:', previousHostDisconnectedAtRef.current);
        }
      }
    };

    const handleHostMovedToNewRoom = (data: { oldRoomName: string; newRoomName: string; message: string }) => {
      console.log('Host moved to new room:', data);
      const current = roomStateRef.current;
      if (!isHostRef.current && current && current.roomName === data.oldRoomName) {
        // Host moved to a new room - redirect player to the new room
        console.log(`Redirecting player from room ${data.oldRoomName} to new room ${data.newRoomName}`);
        showToast(t('toasts.hostMovedToNewRoom', { roomName: data.newRoomName }), 'info', 3000);
        
        // Update session storage with new room name
        const savedPlayerName = RoomSessionStorage.getPlayerName();
        const savedAvatar = RoomSessionStorage.getAvatar();
        
        if (savedPlayerName && socket) {
          // Clear old room data
          RoomSessionStorage.clearAll();
          
          // Set new room data
          RoomSessionStorage.setRoomName(data.newRoomName);
          RoomSessionStorage.setPlayerName(savedPlayerName);
          if (savedAvatar) {
            RoomSessionStorage.setAvatar(savedAvatar);
          }
          RoomSessionStorage.setIsHost(false);
          
          // Join the new room
          setTimeout(() => {
            socket.emit('join-room', {
              roomName: data.newRoomName,
              playerName: savedPlayerName,
              avatar: savedAvatar || undefined,
            });
          }, 500); // Small delay to ensure state is cleared
        }
      }
    };

    const handleLobbyIdleWarning = (data: {
      roomName: string;
      remainingSeconds: number;
      idleTimeoutMs: number;
    }) => {
      const current = roomStateRef.current;
      if (
        isHostRef.current &&
        current &&
        current.roomName === data.roomName &&
        current.gameState === 'waiting'
      ) {
        const seconds = Math.max(0, Math.ceil(Number(data.remainingSeconds) || 0));
        showToast(t('toasts.lobbyIdleWarning', { seconds }), 'warning', 7000);
      }
    };

    const handleFinishedIdleWarning = (data: {
      roomName: string;
      remainingSeconds: number;
      idleTimeoutMs: number;
    }) => {
      const current = roomStateRef.current;
      if (current && current.roomName === data.roomName && current.gameState === 'finished') {
        const seconds = Math.max(0, Math.ceil(Number(data.remainingSeconds) || 0));
        showToast(t('toasts.finishedIdleWarning', { seconds }), 'warning', 8000);
      }
    };

    // ========== GAME EVENTS ==========

    const handleGameStarted = (data: any) => {
      console.log('Game started:', data);
      if (isHostRef.current) playGameStart();
      pendingStartGameRef.current = null;
      // Zero FE scores immediately so Play Again cannot briefly (or permanently via
      // mergePlayersWithoutScoreRegression) show the previous game's totals.
      updateRoomStateForRoom(data.roomName, (prev) => ({
        ...prev,
        gameState: 'playing',
        currentQuestion: null,
        isBonusLevel: false,
        bonusLevelPayload: undefined,
        bonusStartTime: undefined,
        bonusLevelResult: undefined,
        finalLeaderboard: undefined,
        gameStatistics: undefined,
        players: (prev.players ?? []).map((p) => ({ ...p, score: 0 })),
      }));
    };

    // Update room state from bonus-level-start so we show bonus screen even if room-state broadcast was missed
    // (same pattern as re-sending question to reconnected host for normal questions)
    const handleBonusLevelStart = (data: any) => {
      if (isLeavingRef.current) return;
      const savedRoomName = RoomSessionStorage.getRoomName();
      if (!savedRoomName) return;
      setRoomState((prev) => {
        if (!prev || prev.roomName !== savedRoomName) return prev;
        console.log('Bonus level start received, updating room state');
        return {
          ...prev,
          isBonusLevel: true,
          bonusLevelPayload: data,
          bonusStartTime: data.startTime ?? Date.now(),
          bonusLevelResult: undefined,
        };
      });
    };

    const handleBonusPuzzleImage = (data: { imageUrl?: string }) => {
      if (isLeavingRef.current) return;
      const imageUrl = data?.imageUrl;
      if (typeof imageUrl !== 'string') return;
      setRoomState((prev) => {
        if (!prev?.isBonusLevel || !prev.bonusLevelPayload) return prev;
        return {
          ...prev,
          bonusLevelPayload: { ...prev.bonusLevelPayload, imageUrl },
        };
      });
    };

    const mergeBonusLeaderboard = (
      leaderboard: Array<{ playerId: string; playerName: string; score: number }>,
      options?: { requireBonusLevel?: boolean }
    ) => {
      if (!Array.isArray(leaderboard) || leaderboard.length === 0) return;
      setRoomState((prev) => {
        if (!prev) return prev;
        if (
          options?.requireBonusLevel !== false &&
          !prev.isBonusLevel &&
          !prev.bonusLevelResult
        ) {
          return prev;
        }
        const players = syncPlayersFromLeaderboard(prev.players ?? [], leaderboard);
        return { ...prev, players };
      });
    };

    // Merge leaderboard from bonus-level-result so roomState.players have correct scores
    const handleBonusLevelResult = (data: {
      submissions?: Array<{ playerId: string; playerName: string; points: number }>;
      leaderboard?: Array<{ playerId: string; playerName: string; score: number }>;
      bonusGameType?: string;
      questionId?: string;
    }) => {
      if (isLeavingRef.current) return;
      setRoomState((prev) => {
        if (!prev) return prev;
        // Ignore delayed results after Play Again / new game (game-started clears bonus flags).
        // Otherwise a stale leaderboard re-injects previous-game totals into the new game.
        if (!prev.isBonusLevel && !prev.bonusLevelResult && !prev.bonusLevelPayload) {
          return prev;
        }
        const players =
          data?.leaderboard?.length
            ? syncPlayersFromLeaderboard(prev.players ?? [], data.leaderboard)
            : prev.players;
        return {
          ...prev,
          players,
          bonusLevelResult: data,
        };
      });
    };

    // Merge leaderboard from bonus-points-updated (late submissions) for robust score display
    const handleBonusPointsUpdated = (data: { leaderboard?: Array<{ playerId: string; playerName: string; score: number }> }) => {
      if (isLeavingRef.current) return;
      mergeBonusLeaderboard(data?.leaderboard ?? []);
    };

    // bonus-answer-ack is socket-private; apply totalScore so host-as-player strip matches BE immediately.
    // Overlay "+N pts" and standings must stay tied: if BE acks points, totalScore is already in.
    const handleBonusAnswerAck = (data: {
      points?: number;
      totalScore?: number;
      incorrect?: boolean;
    }) => {
      if (isLeavingRef.current) return;
      if (data?.incorrect === true) return;
      if (typeof data?.totalScore !== 'number' || data.totalScore < 0) return;
      const saved = RoomSessionStorage.getSavedRoomData();
      const savedName = saved?.playerName;
      const current = roomStateRef.current;
      const me = savedName
        ? current?.players?.find((p) => p.name === savedName)
        : undefined;
      const playerId = me?.id;
      if (!playerId) return;
      updateRoomState((prev) => {
        if (!prev?.players) return prev;
        // Never apply a previous-game ack after scores were reset for a new game.
        const prevMe = prev.players.find((p) => p.id === playerId);
        if (
          prevMe &&
          (prevMe.score ?? 0) === 0 &&
          data.totalScore! > 0 &&
          !prev.isBonusLevel &&
          !prev.bonusLevelPayload
        ) {
          return prev;
        }
        const players = applyAuthoritativePlayerTotal(prev.players, playerId, data.totalScore!);
        return players ? { ...prev, players } : prev;
      });
    };

    const handleQuestion = (data: any) => {
      console.log('Question received:', data);
      updateRoomState((prev) => {
        if (prev) {
          // During bonus / outro, ignore stale re-sends of the main question that triggered
          // the bonus (reconnect/get-room-state/focus can re-emit Q5/Q10/Q15 and kick the
          // host off the bonus screen while the player timer is still running).
          // Only accept a question that is clearly AFTER the bonus anchor.
          if (prev.isBonusLevel || prev.bonusLevelResult) {
            const bonusAnchor = prev.bonusLevelPayload?.questionNumber;
            const incomingNum = data.questionNumber;
            if (typeof bonusAnchor === 'number' && typeof incomingNum === 'number') {
              if (incomingNum <= bonusAnchor) {
                console.log(
                  'Ignoring question during bonus (stale number)',
                  { bonusAnchor, incomingNum, questionId: data.questionId ?? data.id }
                );
                return prev;
              }
            } else if (prev.isBonusLevel) {
              // Active bonus + missing numbers: hold (safer than kicking host off bonus).
              console.log(
                'Ignoring question during active bonus (missing numbers)',
                { bonusAnchor, incomingNum, questionId: data.questionId ?? data.id }
              );
              return prev;
            }
            // Outro hold (isBonusLevel already cleared) + missing numbers: accept so we don't stick.
          }

          // CRITICAL: Do NOT emit get-room-state here. We already have room state and the question.
          // Emitting would trigger backend to send room-state + an extra 'question' event. That extra
          // question (from get-room-state handler) lacked clues for What Could It Be, overwriting our
          // good question and causing clues to flash/disappear. Also caused console spam in prod.
          const prevQ = prev.currentQuestion;
          const mergedQuestion = {
            ...data,
            // Preserve "Question X of Y" when a reconnect payload omits numbers (host "— of —" bug).
            questionNumber:
              typeof data.questionNumber === 'number'
                ? data.questionNumber
                : prevQ?.questionNumber,
            totalQuestions:
              typeof data.totalQuestions === 'number'
                ? data.totalQuestions
                : prevQ?.totalQuestions ?? prev.bonusLevelPayload?.totalQuestions,
          };
          return {
            ...prev,
            gameState: 'playing',
            currentQuestion: mergedQuestion,
            // Update questionStartTime from question event if provided (for reconnection scenarios)
            questionStartTime: data.startTime || prev.questionStartTime,
            // Clear bonus state: receiving a normal question means bonus is over
            isBonusLevel: false,
            bonusLevelPayload: undefined,
            bonusStartTime: undefined,
            bonusLevelResult: undefined,
          };
        } else {
          const savedRoomName = RoomSessionStorage.getRoomName();
          if (savedRoomName) {
            console.log('Question received before room state, requesting room state for:', savedRoomName);
            socket.emit('get-room-state', { roomName: savedRoomName });
          }
        }
        return prev;
      });

      // Only sync to 'playing' when we receive a question if we're not already in 'finished'
      // (never overwrite finished with playing – e.g. stale question after game ended)
      if (roomStateRef.current?.gameState !== 'finished') {
        updateRoomState((prev) => {
          if (prev) {
            return { ...prev, gameState: 'playing' };
          }
          return prev;
        });
      }
    };

    const handleQuestionResult = (data: any) => {
      console.log('Question result:', data);
      updateRoomState((prev) => {
        if (!prev) return prev;
        // Merge leaderboard from result into room state so scores are correct before
        // bonus-level-start switches to BonusHostScreen (which uses roomState.players).
        // Otherwise we'd show a drop when bonus screen loads if room-state hasn't arrived yet.
        // Leaderboard is authoritative roster + scores (server excludes intentionally-left players).
        const leaderboard = data?.leaderboard as Array<{ playerId: string; playerName: string; score: number }> | undefined;
        const players = syncPlayersFromLeaderboard(prev.players ?? [], leaderboard);
        return {
          ...prev,
          currentQuestion: null,
          gameState: 'playing',
          players: players ?? prev.players,
        };
      });
    };

    const handleGameEnded = (data: any) => {
      console.log('Game ended:', data);
      if (isHostRef.current) playGameFinished();
      updateRoomState((prev) => {
        if (prev) {
          return {
            ...prev,
            gameState: 'finished',
            currentQuestion: null,
            isBonusLevel: false,
            finalLeaderboard: data.leaderboard ?? prev.players?.map((p: any) => ({ playerId: p.id, playerName: p.name, score: p.score })) ?? [],
            gameStatistics: data.statistics ?? undefined,
          };
        }
        return prev;
      });
    };

    const handleGamePaused = (data: { roomName: string }) => {
      console.log('Game paused:', data);
      updateRoomState((prev) => {
        if (!prev || prev.roomName !== data.roomName) return prev;
        return { ...prev, isPaused: true };
      });
    };

    const handleGameResumed = (data: { roomName: string }) => {
      console.log('Game resumed:', data);
      updateRoomState((prev) => {
        if (!prev || prev.roomName !== data.roomName) return prev;
        return { ...prev, isPaused: false };
      });
    };

    const handleReturnToWaiting = (data: { roomName: string }) => {
      console.log('Returning to waiting:', data);
      if (isHostRef.current) playReturnToWaiting();
      // Back to lobby is not leaving the room — clear stale flag (e.g. from beforeunload/tab switch)
      isLeavingRef.current = false;
      if (data.roomName) {
        // Don't use sessionStorage for isHost - it's shared across tabs. If we set isHost from
        // sessionStorage here, the player tab could show host UI (e.g. GameSelection) when it
        // receives return-to-waiting and sessionStorage was overwritten by the host tab.
        setShowJoinRoom(false);
        updateRoomStateForRoom(data.roomName, (prev) => ({
          ...prev,
          gameState: 'waiting',
          currentQuestion: null,
          questionStartTime: undefined,
          isBonusLevel: false,
          bonusLevelPayload: undefined,
          bonusStartTime: undefined,
          isPaused: false,
          finalLeaderboard: undefined,
          gameStatistics: undefined,
        }));
        const savedData = RoomSessionStorage.getSavedRoomData();
        setTimeout(() => {
          if (savedData.hostToken) {
            socket.emit('get-room-state', { roomName: data.roomName, hostToken: savedData.hostToken });
          } else {
            socket.emit('get-room-state', { roomName: data.roomName });
          }
        }, 100);
        // isHost will be set correctly when room-state arrives (socket.id === state.hostId)
      }
    };

    const handleGameError = (error: { message: string }) => {
      console.error('Game error:', error);
      if (error.message === 'Room not found' && pendingStartGameRef.current) {
        const savedRoom = RoomSessionStorage.getSavedRoomData().roomName;
        const roomCode = savedRoom ? formatRoomCode(savedRoom) : undefined;
        const gameType = pendingStartGameRef.current.gameType;
        console.log('Room not found when starting game, creating new room with gameType:', gameType);
        // Use scheduleCreateRoom so create-room is emitted on the new socket after DO routing,
        // not on the socket being closed by reconnectTo.
        if (roomCode && onScheduleCreateRoom) {
          onScheduleCreateRoom({ gameType, roomCode });
        } else if (roomCode && onReconnectTo) {
          onReconnectTo(roomCode);
          // Fallback: only emit if we could not schedule (should be rare)
          socket.emit('create-room', { gameType, roomCode });
        } else {
          socket.emit('create-room', { gameType, ...(roomCode ? { roomCode } : {}) });
        }
        return;
      }
      // Don't show toast for premium errors - frontend handles redirect
      if (error.message && error.message.toLowerCase().includes('premium')) {
        console.log('Premium error from backend - frontend will handle redirect');
        pendingStartGameRef.current = null;
        return;
      }
      // Dedupe "game type not available" toast so we don't spam when BE was rate-limited
      if (error.message && error.message.includes('This game type is not available')) {
        const now = Date.now();
        if (now - lastGameTypeNotAllowedToastRef.current < GAME_TYPE_NOT_ALLOWED_TOAST_DEBOUNCE_MS) {
          pendingStartGameRef.current = null;
          return;
        }
        lastGameTypeNotAllowedToastRef.current = now;
      }
      const gameErrorMessage = error.message
        ? (error.message.includes('This game type is not available')
            ? t('toasts.gameTypeNotAvailable')
            : translateBackendError(error.message))
        : t('toasts.gameError');
      showToast(gameErrorMessage, 'error');
      pendingStartGameRef.current = null;
    };

    const handleGameTypeError = (error: { message: string }) => {
      console.error('Game type error:', error);
      const msg = error.message
        ? (error.message.includes('This game type is not available')
            ? t('toasts.gameTypeNotAvailable')
            : translateBackendError(error.message))
        : t('toasts.gameTypeNotAvailable');
      const now = Date.now();
      if (now - lastGameTypeNotAllowedToastRef.current < GAME_TYPE_NOT_ALLOWED_TOAST_DEBOUNCE_MS) return;
      lastGameTypeNotAllowedToastRef.current = now;
      showToast(msg, 'error');
    };

    const handleRoomSettingsUpdated = (data: {
      roomName: string;
      settings: any;
      branding?: RoomState['branding'] | null;
    }) => {
      console.log('Room settings updated:', data);
      updateRoomStateForRoom(data.roomName, (prev) => ({
        ...prev,
        settings: data.settings,
        ...('branding' in data
          ? { branding: data.branding || undefined }
          : {}),
      }));
    };

    /** Host-only: full company bank with answers (public room-state strips them). */
    const handleCompanyQuestionsHost = (data: {
      roomName?: string;
      questions?: RoomState['companyQuestions'];
    }) => {
      if (!data.questions?.length || !data.roomName) return;
      updateRoomStateForRoom(data.roomName, (prev) => ({
        ...prev,
        companyQuestions: data.questions,
      }));
    };

    const handleTeamError = (data: { message?: string }) => {
      const msg = data?.message?.trim();
      showToast(msg || t('toasts.teamError'), 'error');
    };

    // ========== REGISTER ALL EVENT HANDLERS ==========

    // Wrap so Socket.io/native never pass a 2nd arg into handleConnect's options slot.
    const onConnect = (data?: any) => handleConnect(data);
    socket.on('connect', onConnect);
    socket.on('connect_error', handleConnectError);

    // Catch-up for "connect fired before handlers registered" (socket.connected already true).
    // Real future connects are handled by onConnect. One delayed retry only when not yet
    // connected covers native WS open→ID gap where .connected can flip before 'connect'.
    const runCatchUpIfConnected = () => {
      if (socket.connected) {
        handleConnect({ id: socket.id ?? undefined }, { silent: true });
      }
    };
    const alreadyConnected = socket.connected;
    runCatchUpIfConnected();
    const tCatchUp = alreadyConnected
      ? null
      : setTimeout(runCatchUpIfConnected, 500);
    socket.on('disconnect', handleDisconnect);
    socket.on('room-created', handleRoomCreated);
    socket.on('room-joined', handleRoomJoined);
    socket.on('game-type-changed', handleGameTypeChanged);
    socket.on('votes-updated', handleVotesUpdated);
    socket.on('category-votes-updated', handleCategoryVotesUpdated);
    socket.on('player-joined', handlePlayerJoined);
    socket.on('player-left', handlePlayerLeft);
    socket.on('room-state', handleRoomState);
    socket.on('room-error', handleRoomError);
    socket.on('premium-room-not-activated', handlePremiumRoomNotActivated);
    socket.on('room-destroyed', handleRoomDestroyed);
    socket.on('host-disconnected', handleHostDisconnected);
    socket.on('host-moved-to-new-room', handleHostMovedToNewRoom);
    socket.on('lobby-idle-warning', handleLobbyIdleWarning);
    socket.on('finished-idle-warning', handleFinishedIdleWarning);
    socket.on('game-started', handleGameStarted);
    socket.on('bonus-level-start', handleBonusLevelStart);
    socket.on('bonus-puzzle-image', handleBonusPuzzleImage);
    socket.on('bonus-level-result', handleBonusLevelResult);
    socket.on('bonus-points-updated', handleBonusPointsUpdated);
    socket.on('bonus-answer-ack', handleBonusAnswerAck);
    socket.on('question', handleQuestion);
    socket.on('question-result', handleQuestionResult);
    socket.on('game-ended', handleGameEnded);
    socket.on('game-paused', handleGamePaused);
    socket.on('game-resumed', handleGameResumed);
    socket.on('return-to-waiting', handleReturnToWaiting);
    socket.on('game-error', handleGameError);
    socket.on('game-type-error', handleGameTypeError);
    socket.on('room-settings-updated', handleRoomSettingsUpdated);
    socket.on('company-questions-host', handleCompanyQuestionsHost);
    socket.on('team-error', handleTeamError);

    // ========== CLEANUP ==========

    return () => {
      if (tCatchUp) clearTimeout(tCatchUp);
      socket.off('connect', onConnect);
      socket.off('connect_error', handleConnectError);
      socket.off('disconnect', handleDisconnect);
      socket.off('room-created', handleRoomCreated);
      socket.off('room-joined', handleRoomJoined);
      socket.off('game-type-changed', handleGameTypeChanged);
      socket.off('votes-updated', handleVotesUpdated);
      socket.off('category-votes-updated', handleCategoryVotesUpdated);
      socket.off('player-joined', handlePlayerJoined);
      socket.off('player-left', handlePlayerLeft);
      socket.off('room-state', handleRoomState);
      socket.off('room-error', handleRoomError);
      socket.off('premium-room-not-activated', handlePremiumRoomNotActivated);
      socket.off('room-destroyed', handleRoomDestroyed);
      socket.off('host-disconnected', handleHostDisconnected);
      socket.off('host-moved-to-new-room', handleHostMovedToNewRoom);
      socket.off('lobby-idle-warning', handleLobbyIdleWarning);
      socket.off('finished-idle-warning', handleFinishedIdleWarning);
      socket.off('game-started', handleGameStarted);
      socket.off('bonus-level-start', handleBonusLevelStart);
      socket.off('bonus-puzzle-image', handleBonusPuzzleImage);
      socket.off('bonus-level-result', handleBonusLevelResult);
      socket.off('bonus-points-updated', handleBonusPointsUpdated);
      socket.off('bonus-answer-ack', handleBonusAnswerAck);
      socket.off('question', handleQuestion);
      socket.off('question-result', handleQuestionResult);
      socket.off('game-ended', handleGameEnded);
      socket.off('game-paused', handleGamePaused);
      socket.off('game-resumed', handleGameResumed);
      socket.off('return-to-waiting', handleReturnToWaiting);
      socket.off('game-error', handleGameError);
      socket.off('game-type-error', handleGameTypeError);
      socket.off('room-settings-updated', handleRoomSettingsUpdated);
      socket.off('company-questions-host', handleCompanyQuestionsHost);
      socket.off('team-error', handleTeamError);
    };
    // Note: We intentionally don't include all setters/refs in deps to avoid re-registering
    // The handlers use the latest values via closures. Including them would cause
    // unnecessary re-registration of all event handlers on every state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    socket,
    showToast,
    startReconnection,
    clearReconnection,
    onReconnectTo,
    onScheduleCreateRoom,
    onHostDeepLinkSettled,
  ]);
}
